import { useState, useCallback, useEffect } from 'react';
import { SolarSimulator } from './components/SolarSimulator';
import { GenerationCalculator } from './components/GenerationCalculator';
import { Analytics } from './components/Analytics';
import { MapView } from './components/MapView';
import { WeatherPanel } from './components/WeatherPanel';
import { AudioController } from './components/AudioController';
import { AboutModal } from './components/AboutModal';
import type { SimulationResult } from './hooks/useSolarSimulation';
import { loadProjects, type Project, type ProjectConfig } from './utils/projects';
import './App.css';

function App() {
  // Project state
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  
  // Derived state from project
  const [lat, setLat] = useState(52.50248);  // Hempnall default
  const [lon, setLon] = useState(1.29835);
  const [area, setArea] = useState(2700);
  const [projectConfig, setProjectConfig] = useState<ProjectConfig | null>(null);
  
  // UI state
  const [simulation, setSimulation] = useState<SimulationResult | null>(null);
  const [mapZoom, setMapZoom] = useState(12);
  const [activeTab, setActiveTab] = useState<'simulator' | 'calculator' | 'analytics'>('simulator');
  const [showAboutModal, setShowAboutModal] = useState(false);

  // Load projects on mount
  useEffect(() => {
    loadProjects().then(data => {
      setProjects(data.projects);
      const defaultProject = data.projects.find(p => p.isDefault) || data.projects[0];
      if (defaultProject) {
        selectProject(defaultProject);
      }
    });
  }, []);

  // Select a project
  const selectProject = useCallback((project: Project) => {
    setCurrentProject(project);
    setLat(project.location.lat);
    setLon(project.location.lon);
    setArea(project.config.farm_area_acres);
    setProjectConfig(project.config);
    // Clear simulation when project changes - user needs to run it again
    setSimulation(null);
  }, []);

  const handleLocationChange = useCallback((newLat: number, newLon: number) => {
    setLat(newLat);
    setLon(newLon);
  }, []);

  const handleSimulationUpdate = useCallback((result: SimulationResult | null) => {
    setSimulation(result);
  }, []);

  // Extract values for components
  const currentPower = simulation?.current_power_mw ?? 0;
  const peakPower = simulation?.peak_power_mw ?? 45;
  const efficiency = simulation?.current_efficiency ?? 0;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <a href="/BEPS-DEMO/" className="header-logo">
              <img
                src="../assets/images/Logos%20Banners/BEPS%20Logo.png"
                alt="Block East Pye Solar Campaign Logo"
                className="header-logo-img"
              />
            </a>
          </div>
          <div className="header-right">
            {projects.length > 0 && (
              <select
                className="header-project-selector"
                value={currentProject?.id || ''}
                onChange={(e) => {
                  const project = projects.find(p => p.id === e.target.value);
                  if (project) selectProject(project);
                }}
                title="Select solar project"
              >
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}
            <button
              className="about-btn"
              onClick={() => setShowAboutModal(true)}
              title="View calculation methodology"
            >
              ?
            </button>
            <AudioController
              currentPowerMW={currentPower}
              peakPowerMW={peakPower}
              mapZoom={mapZoom}
            />
            <a
              href="/BEPS-DEMO/"
              className="back-link"
              title="Return to main site"
            >
              Back to Site
            </a>
          </div>
        </div>
      </header>

      <main className="app-main">
        {/* Project Info Banner */}
        {currentProject && (
          <div className="project-banner">
            <div className="project-info">
              <span className="project-location">{currentProject.location.name}</span>
              <span className="project-details">
                {currentProject.config.farm_area_acres.toLocaleString()} acres | 
                {currentProject.developer} | 
                Status: {currentProject.status}
              </span>
            </div>
          </div>
        )}

        <div className="main-grid">
          {/* Left Column: Map and Weather */}
          <div className="left-column">
            <MapView
              lat={lat}
              lon={lon}
              areaAcres={area}
              currentPowerMW={currentPower}
              peakPowerMW={peakPower}
              efficiency={efficiency}
              onLocationChange={handleLocationChange}
              onZoomChange={setMapZoom}
            />
            
            <WeatherPanel lat={lat} lon={lon} />
          </div>

          {/* Right Column: Simulator/Calculator/Analytics */}
          <div className="right-column">
            <div className="tab-header">
              <div className="tab-header-left">
                <span className="project-site-label">Project Site:</span>
                <select
                  className="project-site-selector"
                  value={currentProject?.id || ''}
                  onChange={(e) => {
                    const project = projects.find(p => p.id === e.target.value);
                    if (project) selectProject(project);
                  }}
                >
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tab-header-right">
                <button
                  className={`tab-btn ${activeTab === 'simulator' ? 'active' : ''}`}
                  onClick={() => setActiveTab('simulator')}
                >
                  Live Simulator
                </button>
                <button
                  className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
                  onClick={() => setActiveTab('calculator')}
                >
                  Calculator
                </button>
                <button
                  className={`tab-btn analytics-tab ${activeTab === 'analytics' ? 'active' : ''}`}
                  onClick={() => setActiveTab('analytics')}
                >
                  📊 Analytics
                </button>
              </div>
            </div>

            <div className="tab-content">
              {activeTab === 'simulator' && (
                <SolarSimulator
                  lat={lat}
                  lon={lon}
                  projectConfig={projectConfig}
                  currentProject={currentProject}
                  siteUrl={window.location.origin + window.location.pathname}
                  onSimulationUpdate={handleSimulationUpdate}
                />
              )}
              
              {activeTab === 'calculator' && (
                <GenerationCalculator
                  lat={lat}
                  lon={lon}
                  onAreaChange={setArea}
                />
              )}

              {activeTab === 'analytics' && (
                <Analytics />
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <p>
            Solar Reality Demo - Visualizing the {currentProject?.name || 'Solar Farm'} project impact.
            <br />
            <span className="footer-note">
              Data shown uses conservative estimates based on UK Met Office, PVGIS, and ONS data.
              <button 
                className="methodology-link"
                onClick={() => setShowAboutModal(true)}
              >
                View methodology
              </button>
            </span>
          </p>
          <p className="footer-links">
            <a href="/">Block East Pye Solar</a>
            <span className="separator">|</span>
            <a href="/about-us/index.html">About the Campaign</a>
          </p>
        </div>
      </footer>

      {/* About Modal */}
      <AboutModal 
        isOpen={showAboutModal} 
        onClose={() => setShowAboutModal(false)} 
      />
    </div>
  );
}

export default App;
